import React, { useState, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  Space,
  Avatar,
  Tag,
  Progress,
  message,
  Empty,
  Spin,
  Tooltip,
  Radio,
  Slider,
  Select,
  Badge,
  Row,
  Col
} from 'antd';
import {
  SearchOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  LineChartOutlined,
  LockOutlined,
  GiftOutlined,
  DeleteOutlined,
  FilterOutlined,
  ClearOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Search } = Input;
const { Option } = Select;

interface LineMember {
  id: number;
  line_user_id: string;
  display_name: string;
  member_number: string;
  points: number;
  achievement_count?: number;
}

interface Achievement {
  id: number;
  code: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  condition_type: string;
  condition_value: number;
  rarity: string;
  reward_points: number;
  is_hidden: boolean;
}

interface UserAchievement extends Achievement {
  earned_at?: string;
  current_value?: number;
  progress?: number;
}

const UserAchievementManager: React.FC = () => {
  const [members, setMembers] = useState<LineMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<LineMember | null>(null);
  const [earnedAchievements, setEarnedAchievements] = useState<UserAchievement[]>([]);
  const [progressAchievements, setProgressAchievements] = useState<UserAchievement[]>([]);
  const [lockedAchievements, setLockedAchievements] = useState<UserAchievement[]>([]);
  const [totalAchievementCount, setTotalAchievementCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // フィルタ state
  const [filterAchievementStatus, setFilterAchievementStatus] = useState('all');
  const [filterPointRange, setFilterPointRange] = useState<[number, number]>([0, 1000000]);
  const [filterAchievementCount, setFilterAchievementCount] = useState<[number, number]>([0, 50]);
  const [sortBy, setSortBy] = useState('total_earned_desc');
  const [showFilters, setShowFilters] = useState(true);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

  const searchMember = async (value?: string) => {
    const query = value !== undefined ? value : searchQuery;

    // 空欄の場合は全会員検索（フィルタのみ適用）
    if (!query.trim()) {
      message.info('全会員を対象にフィルタを適用します');
    }

    setSearching(true);
    try {
      const params: any = { query };

      // フィルタパラメータ追加
      if (filterAchievementStatus !== 'all') {
        params.achievement_status = filterAchievementStatus;
      }
      params.min_points = filterPointRange[0];
      params.max_points = filterPointRange[1];
      params.min_achievements = filterAchievementCount[0];
      params.max_achievements = filterAchievementCount[1];
      params.sort_by = sortBy;

      const response = await axios.get(`${API_URL}/line/search`, { params });

      if (response.data.length === 0) {
        message.warning('該当する会員が見つかりません');
        setSelectedMember(null);
        setMembers([]);
      } else if (response.data.length === 1) {
        setSelectedMember(response.data[0]);
        setMembers([]);
        fetchUserAchievements(response.data[0].line_user_id);
      } else {
        setMembers(response.data);
        message.info(`${response.data.length}件の会員が見つかりました`);
      }
    } catch (error) {
      message.error('会員検索に失敗しました');
      console.error(error);
    } finally {
      setSearching(false);
    }
  };

  const selectMember = (member: LineMember) => {
    setSelectedMember(member);
    setMembers([]);
    fetchUserAchievements(member.line_user_id);
  };

  const fetchUserAchievements = async (lineUserId: string) => {
    setLoading(true);
    try {
      const [earnedRes, progressRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/achievements/user/${lineUserId}`),
        axios.get(`${API_URL}/achievements/progress/${lineUserId}`),
        axios.get(`${API_URL}/achievements`)
      ]);

      const earned = earnedRes.data.achievements || earnedRes.data || [];
      const progress = progressRes.data.progress || progressRes.data || [];
      const all = allRes.data.achievements || allRes.data || [];

      setEarnedAchievements(earned);
      setTotalAchievementCount(all.length);

      const earnedCodes = earned.map((a: any) => a.code);
      const progressWithDetails = progress
        .filter((p: any) => !earnedCodes.includes(p.code) && p.current_value > 0)
        .map((p: any) => {
          const achDetail = all.find((a: any) => a.code === p.code);
          if (!achDetail) return null;
          return {
            ...achDetail,
            current_value: p.current_value,
            progress: Math.min((p.current_value / achDetail.condition_value) * 100, 100)
          };
        })
        .filter(Boolean);
      setProgressAchievements(progressWithDetails);

      const shownCodes = new Set([...earnedCodes, ...progressWithDetails.map((p: any) => p.code)]);
      const locked = all.filter((a: any) => !shownCodes.has(a.code));
      setLockedAchievements(locked);
    } catch (error) {
      message.error('称号情報の取得に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const grantAchievement = async (achievementCode: string) => {
    if (!selectedMember) return;

    if (!window.confirm(`${selectedMember.display_name} に称号「${achievementCode}」を付与しますか？`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/achievements/unlock`, {
        lineUserId: selectedMember.line_user_id,
        achievementCode: achievementCode
      });
      message.success('称号を付与しました');
      fetchUserAchievements(selectedMember.line_user_id);
    } catch (error: any) {
      message.error(error.response?.data?.error || '称号の付与に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const revokeAchievement = async (achievementCode: string) => {
    if (!selectedMember) return;

    if (!window.confirm(`${selectedMember.display_name} の称号「${achievementCode}」を剥奪しますか？`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_URL}/achievements/revoke`, {
        data: {
          lineUserId: selectedMember.line_user_id,
          achievementCode: achievementCode
        }
      });
      message.success('称号を剥奪しました');
      fetchUserAchievements(selectedMember.line_user_id);
    } catch (error: any) {
      message.error(error.response?.data?.error || '称号の剥奪に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterAchievementStatus('all');
    setFilterPointRange([0, 1000000]);
    setFilterAchievementCount([0, 50]);
    setSortBy('total_earned_desc');
    message.success('フィルタをクリアしました');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filterAchievementStatus !== 'all') count++;
    if (filterPointRange[0] !== 0 || filterPointRange[1] !== 1000000) count++;
    if (filterAchievementCount[0] !== 0 || filterAchievementCount[1] !== 50) count++;
    if (sortBy !== 'total_earned_desc') count++;
    return count;
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return '#8c8c8c';
      case 'rare': return '#1890ff';
      case 'epic': return '#722ed1';
      case 'legendary': return '#faad14';
      default: return '#8c8c8c';
    }
  };

  // フィルタ変更時に自動的に再検索
  useEffect(() => {
    if (searchQuery.trim()) {
      searchMember();
    }
  }, [filterAchievementStatus, filterPointRange[0], filterPointRange[1], filterAchievementCount[0], filterAchievementCount[1], sortBy]);

  // TODO: 将来的にfeature flagやDB設定でアンロック判定に切り替え
  const isUnlocked = false;

  if (!isUnlocked) {
    return (
      <div style={{ padding: '24px' }}>
        {/* プレミアムオプション案内 */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 16,
          padding: '48px 32px',
          color: '#fff',
          textAlign: 'center',
          marginBottom: 24,
          boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F451;</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px 0', letterSpacing: 1 }}>
            プレミアムオプション
          </h1>
          <h2 style={{ fontSize: 20, fontWeight: 400, margin: '0 0 32px 0', opacity: 0.9 }}>
            会員別称号管理
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
            maxWidth: 800,
            margin: '0 auto 32px',
            textAlign: 'left'
          }}>
            {[
              { icon: '\u{1F50D}', title: '会員検索・一覧', desc: '会員ごとの称号獲得状況をフィルタ付きで一覧管理' },
              { icon: '\u{1F3C6}', title: '称号の手動付与', desc: '特別な称号をスタッフが直接付与可能' },
              { icon: '\u{1F5D1}\u{FE0F}', title: '称号の剥奪', desc: '誤って付与した称号の取り消しに対応' },
              { icon: '\u{1F4CA}', title: '進捗リアルタイム確認', desc: '各会員の称号達成進捗をパーセント表示' },
              { icon: '\u{1F3AF}', title: 'エンゲージメント向上', desc: '称号制度で会員のリピート来店を促進' },
              { icon: '\u{1F4CB}', title: 'カテゴリ別管理', desc: '獲得済み・進捗中・未獲得を分類表示' },
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 12,
                padding: '16px 20px',
                backdropFilter: 'blur(10px)',
              }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{item.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{item.title}</div>
                <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 8,
            padding: '12px 32px',
            fontSize: 14,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
          }}>
            &#x1F6A7; 開発検討中 &#x2015; ご契約・お問い合わせは開発担当まで
          </div>
        </div>

        {/* ぼかしプレビュー */}
        <div style={{ position: 'relative' }}>
          <div style={{
            filter: 'blur(6px)',
            opacity: 0.5,
            pointerEvents: 'none',
            userSelect: 'none',
          }}>
            <Card>
              <div style={{ marginBottom: 24 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <h2 style={{ margin: 0, marginBottom: 16 }}>
                      <TrophyOutlined /> 会員別称号管理
                    </h2>
                    <Search
                      placeholder="会員名または会員番号で絞り込み"
                      allowClear
                      enterButton={<SearchOutlined />}
                      size="large"
                      disabled
                      style={{ maxWidth: 500 }}
                    />
                  </div>
                </Space>
              </div>

              {/* ダミーデータプレビュー */}
              <div style={{ marginBottom: 24, backgroundColor: '#f0f2f5', padding: 16, borderRadius: 8 }}>
                <Space align="center">
                  <Avatar size={64} style={{ backgroundColor: '#1890ff' }}>S</Avatar>
                  <div>
                    <h3 style={{ margin: 0 }}>サンプル会員</h3>
                    <p style={{ margin: 0, color: '#8c8c8c' }}>会員番号: W0001</p>
                    <p style={{ margin: 0 }}>獲得称号: <strong>5</strong> / 17</p>
                  </div>
                </Space>
              </div>

              <h3><CheckCircleOutlined style={{ color: '#52c41a' }} /> 獲得済み称号</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                {['初来店マスター', 'ポイントコレクター', '常連さん'].map((name, i) => (
                  <Card key={i} size="small" style={{ borderLeft: '4px solid #1890ff' }}>
                    <Space>
                      <span style={{ fontSize: 32 }}>{['\u{1F389}', '\u{1F4B0}', '\u2B50'][i]}</span>
                      <div>
                        <h4 style={{ margin: 0 }}>{name}</h4>
                        <Tag color="#1890ff">rare</Tag>
                      </div>
                    </Space>
                  </Card>
                ))}
              </div>

              <h3 style={{ marginTop: 24 }}><LineChartOutlined style={{ color: '#1890ff' }} /> 進捗中の称号</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                <Card size="small" style={{ borderLeft: '4px solid #722ed1' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <span style={{ fontSize: 32 }}>&#x1F371;</span>
                      <div>
                        <h4 style={{ margin: 0 }}>グルメマスター</h4>
                        <Tag color="#722ed1">epic</Tag>
                      </div>
                    </Space>
                    <Progress percent={65} status="active" />
                  </Space>
                </Card>
              </div>
            </Card>
          </div>

          {/* ロックオーバーレイ */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 10,
          }}>
            <LockOutlined style={{ fontSize: 48, color: '#8c8c8c', opacity: 0.6 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <h2 style={{ margin: 0, marginBottom: 16 }}>
                <TrophyOutlined /> 会員別称号管理
              </h2>
              <Search
                placeholder="会員名または会員番号で絞り込み（オプション）"
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                onSearch={(value) => {
                  setSearchQuery(value);
                  searchMember(value);
                }}
                loading={searching}
                style={{ maxWidth: 500 }}
              />
            </div>

            {/* フィルタセクション */}
            <Card
              size="small"
              title={
                <Space>
                  <FilterOutlined />
                  <span>フィルタ</span>
                  {getActiveFilterCount() > 0 && (
                    <Badge count={getActiveFilterCount()} style={{ backgroundColor: '#1890ff' }} />
                  )}
                </Space>
              }
              extra={
                <Space>
                  <Button
                    size="small"
                    icon={<ClearOutlined />}
                    onClick={clearFilters}
                    disabled={getActiveFilterCount() === 0}
                  >
                    クリア
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    {showFilters ? '閉じる' : '開く'}
                  </Button>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              {showFilters && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>称号獲得状況</div>
                    <Radio.Group
                      value={filterAchievementStatus}
                      onChange={(e) => setFilterAchievementStatus(e.target.value)}
                    >
                      <Radio value="all">全員</Radio>
                      <Radio value="has_achievements">獲得済み</Radio>
                      <Radio value="no_achievements">未獲得</Radio>
                      <Radio value="in_progress">進捗中</Radio>
                    </Radio.Group>
                  </div>

                  <Row gutter={16}>
                    <Col span={12}>
                      <div style={{ marginBottom: 8, fontWeight: 500 }}>
                        ポイント数: {filterPointRange[0]} - {filterPointRange[1]}pt
                      </div>
                      <Slider
                        range
                        min={0}
                        max={1000000}
                        step={100}
                        value={filterPointRange}
                        onChange={(value) => setFilterPointRange(value as [number, number])}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: 8, fontWeight: 500 }}>
                        獲得称号数: {filterAchievementCount[0]} - {filterAchievementCount[1]}個
                      </div>
                      <Slider
                        range
                        min={0}
                        max={50}
                        step={1}
                        value={filterAchievementCount}
                        onChange={(value) => setFilterAchievementCount(value as [number, number])}
                      />
                    </Col>
                  </Row>

                  <div>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>並び順</div>
                    <Select
                      value={sortBy}
                      onChange={setSortBy}
                      style={{ width: 300 }}
                    >
                      <Option value="total_earned_desc">ポイント数（降順）</Option>
                      <Option value="total_earned_asc">ポイント数（昇順）</Option>
                      <Option value="achievement_count_desc">獲得称号数（降順）</Option>
                      <Option value="achievement_count_asc">獲得称号数（昇順）</Option>
                      <Option value="display_name_asc">名前（昇順）</Option>
                    </Select>
                  </div>
                </Space>
              )}
            </Card>

            {/* 検索結果リスト */}
            {members.length > 0 && (
              <div>
                <h4>検索結果: {members.length}件</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                  {members.map((member) => (
                    <Card
                      key={member.id}
                      size="small"
                      hoverable
                      onClick={() => selectMember(member)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Meta
                        avatar={<Avatar style={{ backgroundColor: '#87d068' }}>{member.display_name[0]}</Avatar>}
                        title={member.display_name}
                        description={
                          <div>
                            <div>会員番号: W{String(member.id).padStart(4, '0')}</div>
                            <div>{member.points}pt | 称号: {member.achievement_count || 0}個</div>
                          </div>
                        }
                      />
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </Space>
        </div>

        {/* 選択された会員の称号情報 */}
        {selectedMember && (
          <Spin spinning={loading}>
            <div>
              <Card
                size="small"
                style={{ marginBottom: 24, backgroundColor: '#f0f2f5' }}
              >
                <Space align="center">
                  <Avatar size={64} style={{ backgroundColor: '#1890ff' }}>
                    {selectedMember.display_name[0]}
                  </Avatar>
                  <div>
                    <h3 style={{ margin: 0 }}>{selectedMember.display_name}</h3>
                    <p style={{ margin: 0, color: '#8c8c8c' }}>
                      会員番号: W{String(selectedMember.id).padStart(4, '0')}
                    </p>
                    <p style={{ margin: 0 }}>
                      獲得称号: <strong>{earnedAchievements.length}</strong> /{' '}
                      {totalAchievementCount}
                    </p>
                  </div>
                </Space>
              </Card>

              {/* 獲得済み称号 */}
              <div style={{ marginBottom: 32 }}>
                <h3>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} /> 獲得済み称号
                </h3>
                {earnedAchievements.length === 0 ? (
                  <Empty description="獲得済み称号がありません" />
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                    {earnedAchievements.map((ach) => (
                      <Card
                        key={ach.code}
                        size="small"
                        style={{
                          borderLeft: `4px solid ${getRarityColor(ach.rarity)}`
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Space>
                            <span style={{ fontSize: '32px' }}>{ach.icon}</span>
                            <div>
                              <h4 style={{ margin: 0 }}>{ach.name}</h4>
                              <Tag color={getRarityColor(ach.rarity)}>{ach.rarity}</Tag>
                              {ach.reward_points > 0 && (
                                <Tag color="gold">
                                  <GiftOutlined /> {ach.reward_points}pt
                                </Tag>
                              )}
                            </div>
                          </Space>
                          <p style={{ margin: 0, color: '#8c8c8c' }}>{ach.description}</p>
                          {ach.earned_at && (
                            <small style={{ color: '#8c8c8c' }}>
                              獲得日: {new Date(ach.earned_at).toLocaleDateString('ja-JP')}
                            </small>
                          )}
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => revokeAchievement(ach.code)}
                          >
                            剥奪
                          </Button>
                        </Space>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* 進捗中の称号 */}
              <div style={{ marginBottom: 32 }}>
                <h3>
                  <LineChartOutlined style={{ color: '#1890ff' }} /> 進捗中の称号
                </h3>
                {progressAchievements.length === 0 ? (
                  <Empty description="進捗中の称号がありません" />
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                    {progressAchievements.map((ach) => (
                      <Card
                        key={ach.code}
                        size="small"
                        style={{
                          borderLeft: `4px solid ${getRarityColor(ach.rarity)}`
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Space>
                            <span style={{ fontSize: '32px' }}>{ach.icon}</span>
                            <div>
                              <h4 style={{ margin: 0 }}>{ach.name}</h4>
                              <Tag color={getRarityColor(ach.rarity)}>{ach.rarity}</Tag>
                            </div>
                          </Space>
                          <p style={{ margin: 0, color: '#8c8c8c' }}>{ach.description}</p>
                          <div>
                            <Progress
                              percent={Math.round(ach.progress || 0)}
                              status="active"
                            />
                            <small style={{ color: '#8c8c8c' }}>
                              {ach.current_value} / {ach.condition_value}
                            </small>
                          </div>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => grantAchievement(ach.code)}
                          >
                            手動付与
                          </Button>
                        </Space>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* 未獲得称号 */}
              <div>
                <h3>
                  <LockOutlined style={{ color: '#8c8c8c' }} /> 未獲得称号
                </h3>
                {lockedAchievements.length === 0 ? (
                  <Empty description="すべての称号を獲得済みです！" />
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                    {lockedAchievements.map((ach) => (
                      <Card
                        key={ach.code}
                        size="small"
                        style={{
                          borderLeft: `4px solid #d9d9d9`,
                          opacity: ach.is_hidden ? 0.5 : 1
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Space>
                            <span style={{ fontSize: '32px' }}>
                              {ach.is_hidden ? '❓' : ach.icon}
                            </span>
                            <div>
                              <h4 style={{ margin: 0 }}>
                                {ach.is_hidden ? '???' : ach.name}
                              </h4>
                              {ach.is_hidden ? (
                                <Tag>隠し称号</Tag>
                              ) : (
                                <Tag color={getRarityColor(ach.rarity)}>{ach.rarity}</Tag>
                              )}
                            </div>
                          </Space>
                          <p style={{ margin: 0, color: '#8c8c8c' }}>
                            {ach.is_hidden ? '隠し称号です' : ach.description}
                          </p>
                          {!ach.is_hidden && (
                            <small style={{ color: '#8c8c8c' }}>
                              条件: {ach.condition_type} {ach.condition_value}
                            </small>
                          )}
                          <Button
                            type="dashed"
                            size="small"
                            onClick={() => grantAchievement(ach.code)}
                          >
                            手動付与
                          </Button>
                        </Space>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Spin>
        )}
      </Card>
    </div>
  );
};

export default UserAchievementManager;
